import { getNumberValue } from '../service/FormatHelpers/FormatHelpers';
import {
    CurrencyCode,
    RecommendationsDailyBudgetCampaignsRowFragment,
    recommendationsGridSummaryRowFragment,
    RemoveFromBlackListRecommendationsGridRowFragment,
    SwitchOnAutotargetingRecommendationsGridRowFragment
} from '../typings/graphql-grid';
import {
    DailyBudgetRecommendationsItem,
    RecommendationsGridSummaryRecommendation,
    RemovePagesFromBlackListRecommendationsItem,
    SwitchOnAutotargetingRecommendationsItem
} from '../typings/recommendations';

import { getCampaignBudgetFromApiFormat } from './campaign';
import { mapCampaignRowTypeToCampaignType } from './common/common';
import { mapBaseRecommendationInfoFromApiFormat, mapRecommendationKeyFromApiFormat } from './recommendation';

export const mapSummaryGridRecommendationFromApiFormat = (
    apiRecommendation: recommendationsGridSummaryRowFragment
): RecommendationsGridSummaryRecommendation => {
    return {
        ...mapBaseRecommendationInfoFromApiFormat(apiRecommendation),
        totalCount: apiRecommendation.totalCount
    };
};

export function mapDailyBudgetRecommendationsItemFromApiFormat(
    row: RecommendationsDailyBudgetCampaignsRowFragment
): DailyBudgetRecommendationsItem {
    if (row.recommendations === null) {
        throw 'Recommendations not received';
    }

    const recommendation = row.recommendations[0];

    if (
        recommendation.__typename === 'GdRecommendationWithKpi' &&
        recommendation.kpi.__typename === 'GdRecommendationKpiDailyBudget'
    ) {
        return {
            campaignType: mapCampaignRowTypeToCampaignType(row.type),
            name: row.name,
            id: row.id,
            currency: row.currency || CurrencyCode.RUB,
            budget: getCampaignBudgetFromApiFormat(row.strategy.budget),
            status: recommendation.status,
            isApplicable: recommendation.isApplicable,
            budgetRecommendation: getNumberValue(recommendation.kpi.recommendedDailyBudget) || 0,
            budgetRecommendationKeys: recommendation.keys.map(mapRecommendationKeyFromApiFormat),
            clicks: getNumberValue(row.stats.clicks),
            clicksForecast: getNumberValue(recommendation.kpi.clicks),
            cost: getNumberValue(row.stats.cost),
            costForecast: getNumberValue(recommendation.kpi.cost),
            currentDailyBudget: getNumberValue(recommendation.kpi.currentDailyBudget)
        };
    } else {
        throw Error('Invalid recommendation data');
    }
}

export function mapRemovePagesFromBlackListRecommendationsItemFromApiFormat(
    row: RemoveFromBlackListRecommendationsGridRowFragment
): RemovePagesFromBlackListRecommendationsItem {
    if (row.recommendations) {
        const recommendation = row.recommendations[0];

        if (
            recommendation.__typename === 'GdRecommendationWithKpi' &&
            recommendation.kpi.__typename === 'GdRecommendationKpiRemovePagesFromBlackList'
        ) {
            return {
                id: String(row.name),
                name: String(row.name),
                status: recommendation.status,
                isApplicable: recommendation.isApplicable,
                recommendationKeys: recommendation.keys.map(mapRecommendationKeyFromApiFormat),
                currency: recommendation.kpi.currency,
                clicksForecast: getNumberValue(recommendation.kpi.clicks),
                costForecast: getNumberValue(recommendation.kpi.cost)
            };
        }
    }

    throw `Invalid recommendation format for type ${row.__typename}`;
}

export function mapSwitchOnAutotargetingRecommendationsItemFromApiFormat(
    row: SwitchOnAutotargetingRecommendationsGridRowFragment
): SwitchOnAutotargetingRecommendationsItem {
    if (row.recommendations === null) {
        throw Error('Recommendations not received');
    }

    const recommendation = row.recommendations[0];

    if (
        recommendation.__typename === 'GdRecommendationWithKpi' &&
        recommendation.kpi.__typename === 'GdRecommendationKpiSwitchOnAutotargeting'
    ) {
        return {
            name: row.name,
            id: row.id,
            campaign: row.campaign,
            status: recommendation.status,
            isApplicable: recommendation.isApplicable,
            clicks: getNumberValue(row.stats.clicks),
            clicksForecast: getNumberValue(recommendation.kpi.clicks),
            cost: getNumberValue(row.stats.cost),
            costForecast: getNumberValue(recommendation.kpi.cost),
            recommendationKeys: recommendation.keys.map(mapRecommendationKeyFromApiFormat)
        };
    } else {
        throw Error('Invalid recommendation data');
    }
}
